import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import './HOFranchiseeQBSetup.css';

// ─────────────────────────────────────────────────────────────────────────────
// HOFranchiseeQBSetup
//
// PURPOSE:
//   Admin screen to manage each franchisee's:
//     - quickbooksCustomerName  (string)  - the customer name in QuickBooks
//     - vatRate                 (number)  - 15 for SA, 0 for Namibia
//     - isTest                  (boolean) - true for test accounts (hidden from reports)
//
//   These three fields drive the Invoice Export report.
//
// SEED BUTTON:
//   The "Seed Initial Mapping" button does a one-time bulk update using a
//   hardcoded list of the 17 known franchisees + 4 known test accounts.
//   Safe to run multiple times - it only updates fields, never overwrites
//   franchisees that don't match.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Seed data (from QB_acc_names_vs_app_acc_names.xlsx) ──────────────────────
const QB_MAPPING_SEED = {
  'Linda Bos':            { qb: 'Beaux-Arts Centurion Franchise_Linda', vat: 15 },
  'Marilet Landman':      { qb: 'Beaux-Arts Goldfields_Marilet',        vat: 15 },
  'Eileen':               { qb: 'Beaux-Arts KemptonPark_Eileen',        vat: 15 },
  'Ruth van Rensburg':    { qb: 'Beaux-Arts Kimberley_Ruth',            vat: 15 },
  'Cherryll Southey':     { qb: 'Beaux-Arts Midrand_Cherryll',          vat: 15 },
  'Karon Parsons':        { qb: 'Beaux-Arts Overberg_Karon',            vat: 15 },
  'Marie-Louise Lloyd':   { qb: 'Beaux-Arts Parow_ML',                  vat: 15 },
  'Arina Malan':          { qb: 'Beaux-Arts PE_Arina',                  vat: 15 },
  'Cheryl Orchard':       { qb: 'Beaux-Arts Randburg_Cheryl',           vat: 15 },
  'Nina Swanepoel':       { qb: 'Beaux-Arts Roodepoort_Nina',           vat: 15 },
  'Natasha Oosthuysen':   { qb: 'Beaux-Arts Rustenburg_Natasha',        vat: 15 },
  'Dominique Gambale':    { qb: 'Beaux-Arts Sandton_Dominique',         vat: 15 },
  'Alice Neethling':      { qb: 'Beaux-Arts Secunda_Alice',             vat: 15 },
  'Eunice Muller':        { qb: 'Beaux-Arts SJhb_Eunice',               vat: 15 },
  'Maria Potgieter':      { qb: 'Beaux-Arts Vaal 1_Maria',              vat: 15 },
  'Tracey ann':           { qb: 'Beaux-Arts WestCoast_Tracey',          vat: 15 },
  'Michelle Havenga':     { qb: 'Beaux-Arts Windhoek_Michelle',         vat: 0  },  // Namibia - zero rated
};

const TEST_FRANCHISEE_NAMES = ['Lindatjie', 'Linda Test', 'test', 'Santie Nortje'];

function HOFranchiseeQBSetup({ onClose }) {
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [edits, setEdits] = useState({}); // { franchiseeId: { qb, vat, isTest } }
  const [filter, setFilter] = useState('all'); // all | mapped | unmapped | test

  // ── Load franchisees ────────────────────────────────────────────────────────
  useEffect(() => {
    loadFranchisees();
  }, []);

  const loadFranchisees = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'Franchisees'));
      const list = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFranchisees(list);
      setEdits({});
    } catch (err) {
      console.error('Error loading franchisees:', err);
      alert('Could not load franchisees: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Seed initial mapping ────────────────────────────────────────────────────
  const handleSeed = async () => {
    const confirm = window.confirm(
      'This will set QB Customer Name, VAT rate, and test flags for all known ' +
      'franchisees based on the spreadsheet. It will NOT overwrite franchisees ' +
      'who are not in the seed list.\n\nProceed?'
    );
    if (!confirm) return;

    setSeeding(true);
    try {
      const batch = writeBatch(db);
      let mappedCount = 0;
      let testCount = 0;

      for (const f of franchisees) {
        const fRef = doc(db, 'Franchisees', f.id);
        const update = {};

        // QB mapping by exact name match
        if (QB_MAPPING_SEED[f.name]) {
          update.quickbooksCustomerName = QB_MAPPING_SEED[f.name].qb;
          update.vatRate                = QB_MAPPING_SEED[f.name].vat;
          mappedCount++;
        }

        // Test accounts by exact name match
        if (TEST_FRANCHISEE_NAMES.includes(f.name)) {
          update.isTest = true;
          testCount++;
        }

        if (Object.keys(update).length > 0) {
          batch.update(fRef, update);
        }
      }

      await batch.commit();
      alert(`✅ Seeding complete!\n\n${mappedCount} franchisees mapped to QB\n${testCount} marked as test accounts\n\nReloading...`);
      await loadFranchisees();
    } catch (err) {
      console.error('Seeding error:', err);
      alert('❌ Seeding failed: ' + err.message);
    } finally {
      setSeeding(false);
    }
  };

  // ── Get current value (edit if pending, otherwise stored value) ────────────
  const getValue = (f, field) => {
    if (edits[f.id] && edits[f.id][field] !== undefined) {
      return edits[f.id][field];
    }
    return f[field];
  };

  const setEdit = (id, field, value) => {
    setEdits(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const hasEdits = (id) => edits[id] && Object.keys(edits[id]).length > 0;

  // ── Save a single row ──────────────────────────────────────────────────────
  const handleSaveRow = async (f) => {
    if (!hasEdits(f.id)) return;
    setSavingId(f.id);
    try {
      const fRef = doc(db, 'Franchisees', f.id);
      const update = {};
      const e = edits[f.id];
      if (e.quickbooksCustomerName !== undefined) update.quickbooksCustomerName = e.quickbooksCustomerName.trim();
      if (e.vatRate                !== undefined) update.vatRate                = Number(e.vatRate);
      if (e.isTest                 !== undefined) update.isTest                 = !!e.isTest;

      await updateDoc(fRef, update);

      // Reflect in local state
      setFranchisees(prev => prev.map(x => x.id === f.id ? { ...x, ...update } : x));
      setEdits(prev => {
        const next = { ...prev };
        delete next[f.id];
        return next;
      });
    } catch (err) {
      console.error('Save error:', err);
      alert('❌ Save failed: ' + err.message);
    } finally {
      setSavingId(null);
    }
  };

  // ── Counts & filtered list ──────────────────────────────────────────────────
  const counts = {
    total:    franchisees.length,
    mapped:   franchisees.filter(f => f.quickbooksCustomerName && !f.isTest).length,
    test:     franchisees.filter(f => f.isTest).length,
    unmapped: franchisees.filter(f => !f.quickbooksCustomerName && !f.isTest).length,
  };

  const filtered = franchisees.filter(f => {
    if (filter === 'all')       return true;
    if (filter === 'mapped')    return f.quickbooksCustomerName && !f.isTest;
    if (filter === 'unmapped')  return !f.quickbooksCustomerName && !f.isTest;
    if (filter === 'test')      return f.isTest;
    return true;
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="qbsetup-loading">Loading franchisees...</div>;
  }

  return (
    <div className="qbsetup-container">

      {/* Header */}
      <div className="qbsetup-header">
        <div>
          <h1>🧾 QuickBooks Setup</h1>
          <p className="subtitle">
            Map each franchisee to her QuickBooks customer name, set VAT rate, and flag test accounts.
          </p>
        </div>
        <button onClick={onClose} className="back-btn">← Back to Dashboard</button>
      </div>

      {/* Summary cards */}
      <div className="qbsetup-summary">
        <div className="summary-card card-blue">
          <div className="card-label">Total Franchisees</div>
          <div className="card-value">{counts.total}</div>
        </div>
        <div className="summary-card card-green">
          <div className="card-label">✅ Mapped to QB</div>
          <div className="card-value">{counts.mapped}</div>
        </div>
        <div className="summary-card card-orange">
          <div className="card-label">⚠️ Needs Mapping</div>
          <div className="card-value">{counts.unmapped}</div>
        </div>
        <div className="summary-card card-grey">
          <div className="card-label">🧪 Test Accounts</div>
          <div className="card-value">{counts.test}</div>
        </div>
      </div>

      {/* Seed banner - only show if there are unmapped franchisees AND no mapped franchisees yet */}
      {counts.mapped === 0 && counts.unmapped > 0 && (
        <div className="seed-banner">
          <div className="seed-text">
            <strong>🌱 First time setup?</strong>
            <span>Click below to auto-populate QB Customer Names, VAT rates, and test flags from the known mapping.</span>
          </div>
          <button onClick={handleSeed} className="seed-btn" disabled={seeding}>
            {seeding ? 'Seeding...' : '🌱 Seed Initial Mapping'}
          </button>
        </div>
      )}

      {/* Always-available re-seed button (smaller, less prominent) */}
      {counts.mapped > 0 && (
        <div className="reseed-row">
          <button onClick={handleSeed} className="reseed-btn" disabled={seeding}>
            {seeding ? 'Seeding...' : '🔄 Re-run Seed (fills in any blanks from defaults)'}
          </button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="qbsetup-filters">
        <button className={filter === 'all'      ? 'active' : ''} onClick={() => setFilter('all')}>All ({counts.total})</button>
        <button className={filter === 'mapped'   ? 'active' : ''} onClick={() => setFilter('mapped')}>✅ Mapped ({counts.mapped})</button>
        <button className={filter === 'unmapped' ? 'active' : ''} onClick={() => setFilter('unmapped')}>⚠️ Unmapped ({counts.unmapped})</button>
        <button className={filter === 'test'     ? 'active' : ''} onClick={() => setFilter('test')}>🧪 Tests ({counts.test})</button>
      </div>

      {/* Table */}
      <div className="qbsetup-table-wrap">
        <table className="qbsetup-table">
          <thead>
            <tr>
              <th>Franchisee (BABS)</th>
              <th>QuickBooks Customer Name</th>
              <th>VAT %</th>
              <th>Test?</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(f => {
              const qb     = getValue(f, 'quickbooksCustomerName') || '';
              const vat    = getValue(f, 'vatRate');
              const isTest = !!getValue(f, 'isTest');
              const dirty  = hasEdits(f.id);

              let statusLabel = '';
              let statusClass = '';
              if (isTest)            { statusLabel = '🧪 Test';     statusClass = 'status-test'; }
              else if (!qb.trim())   { statusLabel = '⚠️ Unmapped'; statusClass = 'status-unmapped'; }
              else                   { statusLabel = '✅ Mapped';   statusClass = 'status-mapped'; }

              return (
                <tr key={f.id} className={isTest ? 'row-test' : ''}>
                  <td className="cell-name">
                    <div className="franchisee-name">{f.name || '(no name)'}</div>
                    <div className="franchisee-email">{f.email || ''}</div>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="qb-input"
                      value={qb}
                      placeholder="e.g. Beaux-Arts Secunda_Alice"
                      onChange={(e) => setEdit(f.id, 'quickbooksCustomerName', e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      className="vat-select"
                      value={vat === undefined || vat === null ? '' : vat}
                      onChange={(e) => setEdit(f.id, 'vatRate', e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">—</option>
                      <option value="15">15%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td className="cell-center">
                    <label className="test-toggle">
                      <input
                        type="checkbox"
                        checked={isTest}
                        onChange={(e) => setEdit(f.id, 'isTest', e.target.checked)}
                      />
                      <span></span>
                    </label>
                  </td>
                  <td className="cell-center">
                    <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
                  </td>
                  <td className="cell-center">
                    {dirty ? (
                      <button
                        className="save-btn"
                        onClick={() => handleSaveRow(f)}
                        disabled={savingId === f.id}
                      >
                        {savingId === f.id ? 'Saving...' : '💾 Save'}
                      </button>
                    ) : (
                      <span className="saved-tick">✓</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="empty-state">No franchisees match this filter.</div>
        )}
      </div>

    </div>
  );
}

export default HOFranchiseeQBSetup;